import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import axios from 'axios';
import { 
  MapPin, User, Phone, Wifi, WifiOff, Navigation, RefreshCw,
  Wrench, Truck, Car, AlertTriangle, Pause, Play, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Provider type icons
const providerIcons = {
  mechanic: Wrench,
  roadside_tech: Truck,
  roadside_provider: Truck
};

export default function LiveProviderMap({ onSelectProvider }) {
  const { getAuthHeaders } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});

  const fetchProviders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/providers/live-locations`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setProviders(response.data.providers || []);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Load Leaflet
    const loadLeaflet = async () => {
      if (!window.L) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);

        await new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.onload = resolve;
          document.body.appendChild(script);
        });
      }

      const L = window.L;
      
      // Fort Wayne, IN center
      const map = L.map(mapRef.current).setView([41.0793, -85.1394], 11);
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(map);

      mapInstanceRef.current = map;
      updateMarkers();
    };

    loadLeaflet();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update markers when providers change
  const updateMarkers = useCallback(() => {
    if (!mapInstanceRef.current || !window.L) return;

    const L = window.L;
    const map = mapInstanceRef.current;

    // Clear existing markers
    Object.values(markersRef.current).forEach(marker => marker.remove());
    markersRef.current = {};

    // Add markers for each provider
    providers.forEach(provider => {
      const location = provider.location;
      if (!location?.lat || !location?.lng) return;

      // Determine marker color based on status
      let color = provider.is_online ? 'green' : 'gray';
      if (provider.suspension_status === 'suspended') color = 'red';
      else if (provider.suspension_status === 'pending_suspension') color = 'orange';
      else if (provider.active_job) color = 'blue';

      const icon = L.divIcon({
        html: `
          <div style="position: relative;">
            <div style="
              background: ${color};
              width: 36px;
              height: 36px;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-size: 14px;
            ">
              ${provider.user_type === 'mechanic' ? '🔧' : '🚗'}
            </div>
            <div style="
              position: absolute;
              bottom: 100%;
              left: 50%;
              transform: translateX(-50%);
              background: white;
              border-radius: 6px;
              padding: 4px 8px;
              box-shadow: 0 2px 6px rgba(0,0,0,0.2);
              white-space: nowrap;
              font-size: 11px;
              margin-bottom: 4px;
              z-index: 1000;
            ">
              <strong style="color: #1f2937;">${provider.name}</strong>
              <div style="color: #6b7280; font-size: 10px;">${provider.account_type_display || provider.user_type}</div>
              ${provider.registered_address ? `<div style="color: #9ca3af; font-size: 9px; max-width: 150px; overflow: hidden; text-overflow: ellipsis;">${provider.registered_address}</div>` : ''}
            </div>
          </div>
        `,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
        popupAnchor: [0, -20]
      });

      const marker = L.marker([location.lat, location.lng], { icon })
        .addTo(map)
        .bindPopup(`
          <div style="min-width: 200px;">
            <strong>${provider.name}</strong><br/>
            <small>${provider.user_type}</small><br/>
            <span style="color: ${provider.is_online ? 'green' : 'gray'}">
              ${provider.is_online ? '● Online' : '○ Offline'}
            </span>
            ${provider.active_job ? `<br/><em>On Job: ${provider.active_job.service_name}</em>` : ''}
            ${provider.phone ? `<br/>📞 ${provider.phone}` : ''}
          </div>
        `);

      marker.on('click', () => {
        setSelectedProvider(provider);
        if (onSelectProvider) onSelectProvider(provider);
      });

      markersRef.current[provider.user_id] = marker;
    });

    // Fit bounds if we have providers
    const bounds = providers
      .filter(p => p.location?.lat && p.location?.lng)
      .map(p => [p.location.lat, p.location.lng]);
    
    if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [providers, onSelectProvider]);

  useEffect(() => {
    updateMarkers();
  }, [providers, updateMarkers]);

  // Initial fetch and polling
  useEffect(() => {
    fetchProviders();
    const interval = setInterval(fetchProviders, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, [fetchProviders]);

  const onlineCount = providers.filter(p => p.is_online).length;
  const activeJobCount = providers.filter(p => p.active_job).length;
  const suspendedCount = providers.filter(p => p.suspension_status === 'suspended').length;

  return (
    <Card className="h-full" data-testid="live-provider-map">
      <CardHeader className="pb-2 px-3 sm:px-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            <span className="hidden sm:inline">Live Provider Locations</span>
            <span className="sm:hidden">Live Map</span>
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchProviders} className="h-8 w-8 p-0">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 text-xs sm:text-sm mt-2">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 px-2 py-1">
            <Wifi className="w-3 h-3 mr-1" />
            {onlineCount} Online
          </Badge>
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 px-2 py-1">
            <Car className="w-3 h-3 mr-1" />
            {activeJobCount} On Job
          </Badge>
          {suspendedCount > 0 && (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 px-2 py-1">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {suspendedCount} Suspended
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div ref={mapRef} className="h-[300px] sm:h-[400px] w-full rounded-b-lg bg-gray-100" style={{ minHeight: '300px' }} />
      </CardContent>

      {/* Provider Details Panel */}
      {selectedProvider && (
        <div className="p-4 border-t bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                selectedProvider.is_online ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {selectedProvider.user_type === 'mechanic' ? 
                  <Wrench className="w-5 h-5 text-green-600" /> :
                  <Truck className="w-5 h-5 text-orange-600" />
                }
              </div>
              <div>
                <p className="font-semibold">{selectedProvider.name}</p>
                <p className="text-sm text-gray-500">{selectedProvider.user_type}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedProvider(null)}>
              ×
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              {selectedProvider.is_online ? 
                <Wifi className="w-4 h-4 text-green-500" /> :
                <WifiOff className="w-4 h-4 text-gray-400" />
              }
              <span>{selectedProvider.is_online ? 'Online' : 'Offline'}</span>
            </div>
            {selectedProvider.phone && (
              <a href={`tel:${selectedProvider.phone}`} className="flex items-center gap-2 text-blue-600">
                <Phone className="w-4 h-4" />
                {selectedProvider.phone}
              </a>
            )}
          </div>

          {selectedProvider.active_job && (
            <div className="mt-3 p-2 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-700">
                Current Job: {selectedProvider.active_job.service_name}
              </p>
              <p className="text-xs text-blue-600">
                Customer: {selectedProvider.active_job.customer_name} • {selectedProvider.active_job.status}
              </p>
            </div>
          )}

          {selectedProvider.suspension_status === 'suspended' && (
            <div className="mt-3 p-2 bg-red-50 rounded-lg flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-700">Account Suspended</span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// Provider List component for sidebar
export function ProviderList({ providers, onSelect, selectedId }) {
  return (
    <div className="space-y-2">
      {providers.map(provider => {
        const Icon = providerIcons[provider.user_type] || User;
        const isSelected = selectedId === provider.user_id;
        
        return (
          <div
            key={provider.user_id}
            onClick={() => onSelect(provider)}
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              isSelected 
                ? 'border-blue-400 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  provider.suspension_status === 'suspended' 
                    ? 'bg-red-100' 
                    : provider.is_online 
                      ? 'bg-green-100' 
                      : 'bg-gray-100'
                }`}>
                  <Icon className={`w-4 h-4 ${
                    provider.suspension_status === 'suspended'
                      ? 'text-red-600'
                      : provider.is_online
                        ? 'text-green-600'
                        : 'text-gray-500'
                  }`} />
                </div>
                <div>
                  <p className="font-medium text-sm">{provider.name}</p>
                  <p className="text-xs text-gray-500">{provider.user_type}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {provider.active_job && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600">
                    On Job
                  </Badge>
                )}
                <div className={`w-2 h-2 rounded-full ${
                  provider.suspension_status === 'suspended'
                    ? 'bg-red-500'
                    : provider.is_online
                      ? 'bg-green-500'
                      : 'bg-gray-400'
                }`} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
